import { DataPointArray, MatSerNumber, NotificationIdentifier } from '@evorait/pace-core';
import { createAction, props } from '@ngrx/store';
import { FormFiltersType } from '../../types/form-filter';
import { FormIdentifier } from '../../types/form-identifier';
import { FormsConstants } from '../../types/forms-constants';
import { ParticipantCopyId, ParticipantParticulars } from '../../types/participant-copy';
import { Recipient } from '../../types/recipient';
import { FormState } from '../state/form.state';

export const navigateToForm = createAction(
    '[Forms] Navigate to PACE Forms',
    props<{
        form: FormIdentifier;
        notification: NotificationIdentifier;
        status: FormsConstants.FORMSTATUS;
    }>(),
);
export const loadForm = createAction(
    '[Forms] Load form',
    props<{
        form: FormIdentifier;
        notification: NotificationIdentifier;
        status: FormsConstants.FORMSTATUS;
        formType: FormsConstants.FORMTYPE;
        language: string;
    }>(),
);
export const loadFormSuccess = createAction('[Forms] Form successfully loaded', props<{ form: Readonly<FormState> }>());
export const loadFormError = createAction(
    '[Forms] Form could not be loaded',
    props<{ error: string; hasLock: boolean; form: FormIdentifier; notification: NotificationIdentifier }>(),
);

export const unloadForm = createAction('[Forms] unload form');
export const updatedXml = createAction('[Forms] Updated XML', props<{ participant: ParticipantCopyId; xml: string }>());
export const refreshXML = createAction(
    '[Forms] Refresh XML of Form',
    props<{
        form: FormIdentifier;
        mainEquipment: MatSerNumber;
        notification: NotificationIdentifier;
        fromCache: boolean;
    }>(),
);
export const refreshXMLSuccess = createAction(
    '[Forms] Refresh XML Success',
    props<{
        form: FormIdentifier;
        mainEquipment: MatSerNumber;
        notification: NotificationIdentifier;
        fromCache: boolean;
    }>(),
);
export const abortForm = createAction(
    '[Forms] Abort form',
    // TODO: formType can be removed once we do not support the legacy PACE Forms anymore
    // We cannot remove the FormType until we remove all the references to this action.
    // The forms-legacy forms is still calling this action => once the pace-forms legacy part of the app will be removed
    // then the formType can also be removed.
    props<{
        form: FormIdentifier;
        formType: FormsConstants.FORMTYPE;
        notification: NotificationIdentifier;
        navigateToDetails?: boolean;
    }>(),
);
export const abortFormSuccess = createAction(
    '[Forms] Form successfully aborted',
    props<{ form: FormIdentifier; notification: NotificationIdentifier; navigateToDetails: boolean }>(),
);
export const saveForm = createAction('[Forms] Save form');
export const saveFormSuccess = createAction(
    '[Forms] Form successfully saved',
    props<{ form: FormIdentifier; notification: NotificationIdentifier }>(),
);
export const submitCurrentForm = createAction(
    '[Forms] Submit form',
    props<{ id: ParticipantCopyId; saveMode: FormsConstants.SUBMIT_STATUS }>(),
);
export const submitFormSuccess = createAction(
    '[Forms] Form successfully submitted',
    props<{
        ids: ParticipantCopyId[];
        form: FormIdentifier;
        notification: NotificationIdentifier;
        saveMode: FormsConstants.SUBMIT_STATUS;
        skipFormListProcessing: boolean;
    }>(),
);

export const afterSubmitFormSuccess = createAction(
    '[Forms] After form has been successfully submitted',
    props<{
        form: FormIdentifier;
        notification: NotificationIdentifier;
    }>(),
);

export const submitFormError = createAction(
    '[Forms] Form submit errors',
    props<{
        id: ParticipantCopyId;
        saveMode: FormsConstants.SUBMIT_STATUS;
        form: FormIdentifier;
        notification: NotificationIdentifier;
    }>(),
);

export const loadFormIntoCache = createAction(
    '[Forms] Load form into cache',
    props<{
        form: FormIdentifier;
        formType: FormsConstants.FORMTYPE;
        notification: NotificationIdentifier;
        status: FormsConstants.FORMSTATUS;
    }>(),
);
export const loadFormIntoCacheSuccess = createAction(
    '[Forms] Form successfully cached',
    props<{
        form: FormIdentifier;
        notification: NotificationIdentifier;
        lockedBy: string;
    }>(),
);

export const loadFormIntoCacheError = createAction('[Forms] caching Form failed');

// trigger downloading the XML for all forms of the current form list
export const loadAllFormXml = createAction(
    '[Forms] Load all form XML',
    props<{ status: FormsConstants.FORMSTATUS; notification: NotificationIdentifier }>(),
);

export const cleanupFormSuccess = createAction(
    '[Forms] Successfully deleted saved form data',
    props<{
        form: FormIdentifier;
        notification: NotificationIdentifier;
        cached: boolean;
    }>(),
);
export const updateTypeFilter = createAction(
    '[Forms] Type filter updated',
    props<{
        elementType: FormsConstants.ELEMENT_TYPE;
        visible: boolean;
    }>(),
);
export const updateFormFilter = createAction(
    '[Forms] Form filter updated',
    props<{ filterType: FormFiltersType; visible: boolean }>(),
);
export const clearFilters = createAction('[Forms] Remove all filters');
export const saveRecipients = createAction(
    '[Forms] Save recipients',
    props<{
        recipients: Recipient[];
    }>(),
);
export const selectParticipant = createAction(
    '[Forms] Select participant',
    props<{
        id: ParticipantCopyId;
    }>(),
);
export const selectParticipantSuccess = createAction('[Forms] Successfully selected participant copy');
export const selectPreviousParticipant = createAction('[Forms] Select the previous participant copy');
export const selectNextParticipant = createAction('[Forms] Select the next participant copy');

export const removeParticipantForm = createAction(
    '[Forms] Delete participant form',
    props<{
        participant: ParticipantCopyId;
    }>(),
);

export const removeParticipant = createAction(
    '[Forms] Delete participant from confirmation',
    props<{
        participant: ParticipantCopyId;
    }>(),
);

export const removeParticipantFromConfirmationSuccess = createAction(
    '[Forms] Successfully deleted participant',
    props<{
        participant: ParticipantCopyId;
    }>(),
);

export const checkParticipantInModal = createAction(
    '[Forms] Participant selected in participant copy modal',
    props<{
        participant: ParticipantCopyId;
    }>(),
);

export const checkAllParticipants = createAction('[Forms] All Participants checked');

export const generateChecklistCopies = createAction(
    '[Forms] Create checklist copies',
    props<{
        selectedParticipants: ParticipantCopyId[];
        employeeSignatureDataPoints: DataPointArray;
        employeeSignature: string;
        employeeFirstName: string;
        employeeLastName: string;
        employeeDate: string;
    }>(),
);
export const generateChecklistCopiesSuccess = createAction('[Forms] Successfully created checklist copies');

export const addOrUpdateParticipantsfromConfirmation = createAction(
    '[Forms] Add or update confirmation participants',
    props<{
        participants: ParticipantParticulars[];
    }>(),
);

export const addParticipantCopy = createAction(
    '[Forms] Add new participant copy',
    props<{
        id: ParticipantCopyId;
    }>(),
);

export const saveParticipantParticulars = createAction(
    '[Forms] Save participant particulars',
    props<{
        participant: ParticipantParticulars;
    }>(),
);

export const saveParticipantParticularsSuccess = createAction('[Forms] Successfully saved participant particulars');

export const updateCurrentlyEditedParticipant = createAction(
    '[Forms] Update currently edited participant',
    props<{
        id: ParticipantCopyId;
    }>(),
);

export const updateSignFieldSuccess = createAction('[Forms] Successfully updated sign field');
export const updateConfirmationAttendeeSubmittedFieldsSuccess = createAction(
    '[Forms] Successfully updated formSubmitted field',
);

export const registerAccordionGroups = createAction(
    '[Forms] Registering accordion groups',
    props<{
        groupIds: string[];
    }>(),
);

export const expandAccordionByGroup = createAction(
    '[Forms] Expand accordion by group',
    props<{
        groupId: string;
    }>(),
);

export const collapseAccordionByGroup = createAction(
    '[Forms] Collapse accordion by group',
    props<{
        groupId: string;
    }>(),
);

export const expandAccordionWithChildren = createAction(
    '[Forms] expanding accordion with children',
    props<{
        groupId: number;
    }>(),
);

export const collapseAccordionWithChildren = createAction(
    '[Forms] collapsing accordion with children',
    props<{
        groupId: number;
    }>(),
);

export const markFormAsEdited = createAction('[Forms] marking form as edited');

export const enableManualIndividualization = createAction('[Forms] enabling manual individualization');

export const updateTopicVisibility = createAction(
    '[Forms] updating topics visibility',
    props<{
        visibility: boolean;
        id: number;
        materialNumber?: string;
        serialNumber?: string;
        description?: string;
    }>(),
);

export const updateFormReadOnlyReason = createAction(
    '[Forms] Updating form readonly reason',
    props<{ readonlyReason: FormsConstants.READONLY_REASON }>(),
);

export const updateServiceActivityType = createAction(
    '[Forms] Updating Service Activity Type',
    props<{
        serviceActivity: FormsConstants.SERVICE_ACTIVITY_TYPES;
    }>(),
);

export const updateSubmitStatus = createAction(
    '[Forms] Updating form submit status',
    props<{
        form: FormState;
        notification: NotificationIdentifier;
        submitStatus: FormsConstants.SUBMIT_STATUS;
    }>(),
);

export const updateFormListForForm = createAction(
    '[Forms] Updating form list for form entry',
    props<{
        form: FormState;
    }>(),
);
